import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import SaviBusinessJsonLd from "../../components/SaviBusinessJsonLd";
import SaviQuiz from "../../components/SaviQuiz";
import { ArrowRight, Monitor, Wifi, Sun, Users, Clock, Mountain, Coffee } from "lucide-react";
import { pic } from "../../lib/pic";

const COWORKING_IMG = "https://customer-assets.emergentagent.com/job_5ed0a8fe-7bde-4cf0-886b-3137a44819c8/artifacts/lkhx9niz_20260401_133049-EDIT.jpg";

const CoworkingValenciaPage = () => {
  return (
    <div className="min-h-screen bg-[#FBF7F1]">
      <Helmet>
        <title>Coworking Near Valencia | Savi — Mountain Workspace for Remote Workers</title>
        <meta name="description" content="A coworking space near Valencia in the mountains. Dual screens, Starlink WiFi (500Mbps), natural light, zero distractions. Part of Savi Coliving. From €1,200/month." />
        <link rel="canonical" href="https://www.savicoliving.com/coworking-valencia" />
        <meta property="og:title" content="Coworking Near Valencia | Savi Coliving" />
        <meta property="og:description" content="Not another WeWork. A mountain workspace with dual screens, 500Mbps WiFi, and people who actually ship." />
        <meta property="og:image" content={COWORKING_IMG} />
      </Helmet>
      <SaviBusinessJsonLd id="coworking-valencia" />

      <header className="fixed top-0 left-0 right-0 z-50 bg-[#FBF7F1]/95 backdrop-blur-lg border-b border-stone-100">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="https://customer-assets.emergentagent.com/job_184a0a73-7ed5-43f8-9a23-0cd69b83d3bb/artifacts/rcargp5m_no%20bkg.png" alt="Savi" className="h-7 w-auto" />
            <span className="text-base tracking-wider text-[#C89B7B]"><span style={{fontFamily: 'Cinzel, serif'}}>SAVI</span> <span style={{fontFamily: "'Cormorant Garamond', serif"}}>Coliving</span></span>
          </Link>
          <Link to="/" className="text-sm text-[#C85A38] font-medium hover:underline">View the house →</Link>
        </div>
      </header>

      {/* HERO */}
      <section className="pt-14">
        <div className="relative h-[70vh] overflow-hidden">
          <img {...pic("coworking")} fetchpriority="high" decoding="async" alt="Coworking space at Savi near Valencia" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#2E2A26] via-[#2E2A26]/40 to-[#2E2A26]/20" />
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-white mb-4 max-w-4xl leading-tight" style={{textShadow: '0 2px 12px rgba(0,0,0,0.5)'}}>
              A workspace that doesn't feel<br className="hidden sm:block" /> like work.
            </h1>
            <p className="text-white/60 text-base sm:text-lg max-w-xl mb-8" style={{textShadow: '0 2px 8px rgba(0,0,0,0.4)'}}>
              Coworking in the mountains near Valencia. Dual screens, 500Mbps WiFi, natural light, and people who actually get things done.
            </p>
            <Link to="/">
              <button className="bg-[#C85A38] hover:bg-[#A84830] text-white rounded-full px-10 py-4 text-sm font-medium tracking-wide transition-all">
                Check availability <ArrowRight className="inline ml-2 w-4 h-4" />
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* THE PROBLEM */}
      <section className="py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <h2 className="font-serif text-2xl sm:text-3xl text-[#2E2A26] mb-5">The remote work lie</h2>
          <p className="text-[#2E2A26]/60 text-sm leading-relaxed mb-4">
            "Work from anywhere" sounds great until you're on your third café of the day, hunting for a plug socket, praying the WiFi holds during a client call. Or you're in a generic <strong className="text-[#2E2A26]/80">coworking space in Valencia</strong> that's basically an open-plan office with a coffee machine and a Spotify playlist.
          </p>
          <p className="text-[#2E2A26]/60 text-sm leading-relaxed">
            You don't need another hot desk. You need an environment where deep work actually happens — and where the people around you are doing the same.
          </p>
        </div>
      </section>

      {/* THE SPACE */}
      <section className="py-12 px-4 bg-[#F4ECE2]">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-serif text-2xl sm:text-3xl text-[#2E2A26] mb-6">The workspace</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { icon: Monitor, title: "Dual screens at every desk", text: "Real monitors, not laptop stands. Ergonomic chairs. Proper workstations designed for 8-hour days." },
              { icon: Wifi, title: "Starlink — 500Mbps", text: "Reliable, fast internet even in the mountains. Video calls, large uploads, streaming — no issues." },
              { icon: Sun, title: "Natural light everywhere", text: "Floor-to-ceiling windows overlooking the mountains. Plants. Warm stone and wood. It doesn't feel like an office." },
              { icon: Users, title: "6-8 people max", text: "Not 200 strangers. A small group of founders and creatives you actually know. Quiet, focused, respectful." }
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-xl p-5 flex gap-4">
                <div className="w-10 h-10 rounded-lg bg-[#C85A38]/10 flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-5 h-5 text-[#C85A38]" />
                </div>
                <div>
                  <p className="text-[#2E2A26] text-sm font-medium mb-1">{item.title}</p>
                  <p className="text-[#2E2A26]/60 text-sm leading-relaxed">{item.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PHOTO */}
      <section className="px-2 sm:px-4 py-2">
        <div className="max-w-4xl mx-auto">
          <img {...pic("coworking", { sizes: "(min-width: 1024px) 896px, 100vw", fallback: 1080 })} loading="lazy" decoding="async" alt="Savi coworking space with dual screens and mountain views" className="w-full h-64 sm:h-96 object-cover rounded-xl" />
        </div>
      </section>

      {/* THE RHYTHM */}
      <section className="py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <h2 className="font-serif text-2xl sm:text-3xl text-[#2E2A26] mb-5">The work rhythm here</h2>
          <p className="text-[#2E2A26]/60 text-sm leading-relaxed mb-4">
            Most people are at their desks by 9. The house is quiet. Coffee's on. By lunchtime, real work has happened — not "busy work," but the deep stuff you've been putting off for weeks.
          </p>
          <p className="text-[#2E2A26]/60 text-sm leading-relaxed mb-4">
            Afternoons flex. Some keep working. Some hit the gym, the pool, or the hiking trails. Weekly skill shares and group accountability sessions keep everyone on track without being forced.
          </p>
          <p className="text-[#2E2A26]/60 text-sm leading-relaxed">
            There's no alcohol. No late nights destroying tomorrow's focus. Just a calm, productive environment where the <strong className="text-[#2E2A26]/80">coworking near Valencia</strong> happens naturally because everyone's aligned.
          </p>
        </div>
      </section>

      {/* NOT JUST A DESK */}
      <section className="py-12 px-4 bg-[#2E2A26]">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-serif text-2xl sm:text-3xl text-white mb-6">Not just a desk — a whole environment</h2>
          <p className="text-white/60 text-sm leading-relaxed mb-6">
            The coworking space is part of Savi Coliving — a private villa in the Sierra Calderona mountains, 25 minutes from Valencia. You don't just get a desk. You get:
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              { icon: Mountain, text: "A private villa in the mountains" },
              { icon: Coffee, text: "Pool, gym, roof terrace" },
              { icon: Users, text: "A curated community of up to 16 people" },
              { icon: Clock, text: "Stays of 1-3 months, not drop-ins" }
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3 bg-white/5 rounded-xl p-4">
                <item.icon className="w-4 h-4 text-[#C85A38] flex-shrink-0" />
                <p className="text-white/70 text-sm">{item.text}</p>
              </div>
            ))}
          </div>
          <p className="text-white/40 text-sm mt-6">
            Rooms from €1,200/month · All utilities, WiFi, and amenities included
          </p>
        </div>
      </section>

      {/* TESTIMONIAL */}
      <section className="py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <blockquote className="border-l-2 border-[#C85A38] pl-5">
            <p className="text-[#2E2A26]/70 text-base leading-relaxed font-serif italic mb-3">
              "A rare environment where you can slow down, connect, and leave clearer, more focused, and recharged than when you arrived. When I'm back in Spain, this is where I'll come home to."
            </p>
            <div className="flex items-center gap-2.5">
              <img src="https://customer-assets.emergentagent.com/job_5ed0a8fe-7bde-4cf0-886b-3137a44819c8/artifacts/tc416uth_travis.jpg" alt="Travis Weeks" className="w-8 h-8 rounded-full object-cover" loading="lazy" />
              <p className="text-[#2E2A26]/90 text-xs font-medium">Travis Weeks <span className="text-[#2E2A26]/40">— Resident, 90-day stay</span></p>
            </div>
          </blockquote>
        </div>
      </section>

      {/* QUIZ */}
      <section className="py-12 px-4 bg-[#FBF7F1]">
        <div className="max-w-xl mx-auto text-center mb-8">
          <p className="text-[#C85A38] text-xs font-medium tracking-[0.2em] uppercase mb-2">30 seconds</p>
          <h2 className="font-serif text-2xl sm:text-3xl text-[#2E2A26] mb-2">Is Savi right for you?</h2>
          <p className="text-[#2E2A26]/50 text-sm">Four quick questions. No sign-up required.</p>
        </div>
        <SaviQuiz />
      </section>

      {/* CTA */}
      <section className="py-14 px-4 bg-[#F4ECE2]">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="font-serif text-3xl sm:text-4xl text-[#2E2A26] mb-3">Ready to do your best work?</h2>
          <p className="text-[#2E2A26]/50 text-sm mb-8 max-w-md mx-auto">
            This isn't a day pass. It's a place to live and work for 1-3 months with people who are building something real. Apply and see if it's the right fit.
          </p>
          <Link to="/">
            <button className="bg-[#C85A38] hover:bg-[#A84830] text-white rounded-full px-12 py-5 text-sm font-medium transition-all">
              View Savi Coliving <ArrowRight className="inline ml-2 w-4 h-4" />
            </button>
          </Link>
        </div>
      </section>

      {/* RELATED */}
      <section className="py-8 px-4 bg-[#FBF7F1]">
        <div className="max-w-3xl mx-auto">
          <p className="text-[#2E2A26]/30 text-xs uppercase tracking-wider mb-3">Related</p>
          <div className="flex flex-wrap gap-3">
            <Link to="/coliving-valencia" className="text-[#C85A38] text-xs hover:underline">Coliving Valencia</Link>
            <span className="text-[#2E2A26]/20">·</span>
            <Link to="/coliving-valencia-for-founders" className="text-[#C85A38] text-xs hover:underline">Coliving for Founders</Link>
            <span className="text-[#2E2A26]/20">·</span>
            <Link to="/quiet-coliving-valencia" className="text-[#C85A38] text-xs hover:underline">Quiet Coliving Valencia</Link>
            <span className="text-[#2E2A26]/20">·</span>
            <Link to="/rooms" className="text-[#C85A38] text-xs hover:underline">Rooms & Pricing</Link>
          </div>
        </div>
      </section>

      <footer className="bg-white py-6 px-4">
        <div className="max-w-3xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-[#2E2A26]/40 text-xs">&copy; 2026 SAVI Coliving</p>
          <div className="flex gap-4 text-xs text-[#2E2A26]/40">
            <Link to="/privacy" className="hover:text-[#2E2A26]">Privacy</Link>
            <Link to="/cookies" className="hover:text-[#2E2A26]">Cookies</Link>
            <Link to="/" className="hover:text-[#2E2A26]">Home</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CoworkingValenciaPage;
